import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { focusElement, getFocusableElems } from "../../utils/keyboardFocus";

interface MasonryInstance {
    layout: () => void;
    reloadItems: () => void;
    addItems: (elements: Element | Element[]) => void;
    appended: (elements: Element | Element[]) => void;
    getItemElements: () => HTMLElement[];
    destroy: () => void;
}

interface MasonryOptions {
    itemSelector?: string;
    columnWidth?: number | string | HTMLElement;
    gutter?: number;
    horizontalOrder?: boolean;
    transitionDuration?: number | string;
    percentPosition?: boolean;
    stamp?: string;
    fitWidth?: boolean;
    originLeft?: boolean;
    originTop?: boolean;
    containerStyle?: Record<string, string>;
    resize?: boolean;
    initLayout?: boolean;
}

function getAllTiles(
    containerRef: React.RefObject<HTMLDivElement | null>,
    itemSelector: string,
): HTMLElement[] {
    if (!containerRef.current) {
        return [];
    }
    const elems =
        containerRef.current.querySelectorAll<HTMLElement>(itemSelector);
    return Array.from(elems);
}

function getMaxTileHeight(tiles: HTMLElement[]): number {
    return tiles.reduce((memo, elem) => {
        return Math.max(memo, elem.offsetHeight);
    }, 0);
}

export function MasonryGrid<Entity>(props: {
    appendItems?: boolean;
    className?: string;
    itemSelector: string;
    entities: readonly Entity[];
    getTileID: (entity: Entity) => string;
    buildTile: (
        entity: Entity,
        ref: React.RefObject<HTMLDivElement | null>,
    ) => React.ReactNode;
    emptyState: React.ReactNode;
    options?: MasonryOptions;
    forceEqualHeight?: boolean;
}): React.ReactNode {
    const tileRefsRef = React.useRef<
        Map<string, React.RefObject<HTMLDivElement | null>>
    >(new Map());
    const containerRef = React.useRef<HTMLDivElement>(null);
    const masonryGridRef = React.useRef<MasonryInstance | null>(null);
    const prevEntitiesRef = React.useRef<readonly Entity[]>([]);
    const imagesWithListeners = React.useRef(new WeakSet<HTMLImageElement>());

    const setTileHeights = () => {
        const tiles = getAllTiles(containerRef, props.itemSelector);
        // Clear set heights so that we can measure the intrinsic height
        tiles.forEach((elem) => {
            elem.style.height = "";
        });
        // Measure the intrinsic height of each tile, then set each tile to the max.
        const height = getMaxTileHeight(tiles);
        tiles.forEach((elem) => {
            elem.style.height = `${height}px`;
        });
    };

    const layout = () => {
        // Set tile heights (if requested)
        if (props.forceEqualHeight) {
            setTileHeights();
        }
        // Relayout grid
        masonryGridRef.current?.layout();
    };

    const reloadItems = () => {
        masonryGridRef.current?.reloadItems();
    };

    const addItems = (elements: Element | Element[]) => {
        masonryGridRef.current?.addItems(elements);
    };

    const appended = (elements: Element | Element[]) => {
        masonryGridRef.current?.appended(elements);
    };

    const attachEventHandlers = () => {
        if (!containerRef.current) {
            return;
        }
        // When images load, posts change size, so we need to recalculate the grid layout.
        const images = Array.from(
            containerRef.current.querySelectorAll("img"),
        ).filter((img) => !imagesWithListeners.current.has(img));
        for (const img of images) {
            imagesWithListeners.current.add(img);
            if (!img.complete) {
                img.addEventListener("load", () => layout(), { once: true });
            }
        }
        // Images already loaded (e.g. from browser cache) won't fire a load
        // event, so trigger a relayout if any are already complete.
        if (images.some((img) => img.complete)) {
            layout();
        }
    };

    const getOrCreateRef = (
        tileID: string,
    ): React.RefObject<HTMLDivElement | null> => {
        let ref = tileRefsRef.current.get(tileID);
        if (ref) {
            return ref;
        }
        ref = React.createRef<HTMLDivElement>();
        tileRefsRef.current.set(tileID, ref);
        return ref;
    };

    const updateGridLayout = () => {
        const oldNodes: HTMLElement[] | null =
            masonryGridRef.current?.getItemElements() ?? null;
        if (!containerRef.current || !oldNodes) {
            return;
        }
        const currentNodes = containerRef.current.querySelectorAll<HTMLElement>(
            props.itemSelector,
        );
        const newNodes = Array.from(currentNodes).filter((node) => {
            return !oldNodes.includes(node);
        });

        if (props.appendItems) {
            // Append new items to the end of layout
            appended(newNodes);
        } else {
            addItems(newNodes);
        }
        reloadItems();
        layout();
        attachEventHandlers();
    };

    const focusFirstNewTile = (prevEntities: readonly Entity[]) => {
        const newEntityArrayIsSuperset = prevEntities.reduce(
            (memo, oldEntity) => {
                return memo && props.entities.indexOf(oldEntity) !== -1;
            },
            true,
        );
        if (!newEntityArrayIsSuperset) {
            return;
        }
        const newEntities = props.entities.filter((newEntity) => {
            return prevEntities.indexOf(newEntity) === -1;
        });
        const entityToFocus: Entity | undefined = newEntities[0];
        if (!entityToFocus) {
            return;
        }
        const tileID = props.getTileID(entityToFocus);
        const tileRef = tileRefsRef.current.get(tileID);
        // Within the chosen tile, find the first focusable element and focus on it.
        if (tileRef && tileRef.current) {
            const firstFocusableElem = getFocusableElems(tileRef.current)[0];
            if (firstFocusableElem) {
                focusElement(firstFocusableElem);
            }
        }
    };

    // componentDidMount
    React.useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        // Only initialize Masonry on the client side
        if (!getWindow()) {
            return;
        }

        let cancelled = false;

        // Dynamically import Masonry to avoid SSR issues.
        // SSR exclusion is handled by `ssr.external` in build-utils/vite.ts and
        // the `getWindow()` guard above; the client build chunks this normally.
        import("masonry-layout")
            .then((MasonryModule) => {
                if (cancelled || !containerRef.current) {
                    return;
                }
                const MasonryConstructor = MasonryModule.default;
                masonryGridRef.current = new MasonryConstructor(
                    containerRef.current,
                    {
                        itemSelector: props.itemSelector,
                        ...props.options,
                    },
                ) as MasonryInstance;

                // Register all DOM items with Masonry (including any that
                // arrived while the async import was in-flight)
                updateGridLayout();
            })
            .catch((error) => {
                if (cancelled) return;
                console.error("Failed to load Masonry:", error);
                // Fallback: existing .post CSS with float layout provides graceful degradation
            });

        // componentWillUnmount
        return () => {
            cancelled = true;
            // Destroy the grid layout calculator before unloading
            masonryGridRef.current?.destroy();
        };
        // Empty dependency array is intentional - this should only run once on mount.
        // Masonry doesn't support option updates after initialization, and re-initializing
        // would be expensive. Props like itemSelector, options, and forceEqualHeight
        // typically don't change during the component lifecycle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentDidUpdate
    React.useEffect(() => {
        // Skip if Masonry hasn't loaded yet -- initialization effect handles first layout
        if (!masonryGridRef.current) {
            prevEntitiesRef.current = props.entities;
            return;
        }
        // Update the grid layout in case theres new items
        updateGridLayout();
        // If there's no tiles currently shown, there's nothing more to do here.
        if (props.entities.length <= 0) {
            prevEntitiesRef.current = props.entities;
            return;
        }
        // Set tile heights (if requested)
        if (props.forceEqualHeight) {
            setTileHeights();
        }
        // Try to focus on first tile of new content (if we just loaded a new page of tiles).
        focusFirstNewTile(prevEntitiesRef.current);

        prevEntitiesRef.current = props.entities;
    }, [
        props.entities,
        props.forceEqualHeight,
        props.itemSelector,
        props.appendItems,
    ]);

    return (
        <div className={`masonry-grid ${props.className}`} ref={containerRef}>
            {props.entities.map((entity) => {
                const tileID = props.getTileID(entity);
                const ref = getOrCreateRef(tileID);
                return (
                    <React.Fragment key={tileID}>
                        {props.buildTile(entity, ref)}
                    </React.Fragment>
                );
            })}
            {props.entities.length <= 0 && props.emptyState}
        </div>
    );
}
